<script setup>
// The custom tools as the kit's table (phase 4.7, R2-4-7 = A): name (and the address it calls), description,
// authentication, when it changed, and the kit's ⋮ menu (edit, delete) for admins, as the old card had. Rows are
// data from the API; the page decides what the actions do ({ action, id }, same as the old card).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ptBR } from 'date-fns/locale';
import { Edit16, Locked16, TrashCan16 } from '@carbon/icons-vue';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { usePolicy } from 'dashboard/composables/usePolicy';
import RowMenu from 'dashboard/components-next/polaris/cards/RowMenu.vue';
import MiddleTruncate from 'dashboard/components-next/polaris/cards/MiddleTruncate.vue';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'dashboard/components-next/polaris/ui/table';

defineProps({
  tools: { type: Array, required: true },
});
const emit = defineEmits(['action']);

const { t, locale } = useI18n();
const { checkPermissions } = usePolicy();
const canManage = computed(() => checkPermissions(['administrator']));
const dateLocale = computed(() =>
  locale.value === 'pt_BR' ? ptBR : undefined
);

const AUTH = {
  none: 'POLARIS.CUSTOM_TOOLS.FORM.AUTH_TYPES.NONE',
  bearer: 'POLARIS.CUSTOM_TOOLS.FORM.AUTH_TYPES.BEARER',
  basic: 'POLARIS.CUSTOM_TOOLS.FORM.AUTH_TYPES.BASIC',
  api_key: 'POLARIS.CUSTOM_TOOLS.FORM.AUTH_TYPES.API_KEY',
};
const authLabel = type => t(AUTH[type] || AUTH.none);
const updated = tool =>
  dynamicTime(tool.updated_at || tool.created_at, dateLocale.value);

const items = computed(() => [
  {
    label: t('POLARIS.CUSTOM_TOOLS.OPTIONS.EDIT_TOOL'),
    value: 'edit',
    icon: Edit16,
  },
  {
    label: t('POLARIS.CUSTOM_TOOLS.OPTIONS.DELETE_TOOL'),
    value: 'delete',
    icon: TrashCan16,
    destructive: true,
  },
]);
</script>

<template>
  <!-- Columns follow the room the table has, not the viewport: with the tool drawer open as a column the list
       is narrow on a wide screen (container queries). -->
  <div
    class="overflow-hidden rounded-polaris-xl border border-solid border-polaris-border bg-polaris-card @container"
    data-tools-table
  >
    <Table class="table-fixed">
      <TableHeader>
        <TableRow>
          <TableHead class="ps-4 @2xl:w-[34%]">
            {{ t('POLARIS.PAGES.TOOLS_TABLE.NAME') }}
          </TableHead>
          <TableHead class="hidden @2xl:table-cell">
            {{ t('POLARIS.PAGES.TOOLS_TABLE.DESCRIPTION') }}
          </TableHead>
          <TableHead class="hidden w-36 @md:table-cell">
            {{ t('POLARIS.PAGES.TOOLS_TABLE.AUTH') }}
          </TableHead>
          <TableHead class="hidden w-40 @3xl:table-cell">
            {{ t('POLARIS.PAGES.TOOLS_TABLE.UPDATED') }}
          </TableHead>
          <TableHead v-if="canManage" class="w-12 pe-2">
            <span class="sr-only">{{
              t('POLARIS.PAGES.TOOLS_TABLE.ACTIONS')
            }}</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow v-for="tool in tools" :key="tool.id">
          <TableCell class="ps-4 align-top">
            <p class="truncate type-polaris-body-medium" :title="tool.title">
              {{ tool.title }}
            </p>
            <MiddleTruncate
              :text="`${tool.http_method} ${tool.endpoint_url}`"
              class="font-mono type-polaris-caption text-polaris-muted-foreground"
            />
          </TableCell>
          <TableCell class="hidden align-top @2xl:table-cell">
            <p
              class="line-clamp-2 whitespace-normal break-words text-polaris-muted-foreground"
            >
              {{ tool.description }}
            </p>
          </TableCell>
          <TableCell class="hidden align-top @md:table-cell">
            <span class="inline-flex items-center gap-1">
              <Locked16
                v-if="tool.auth_type && tool.auth_type !== 'none'"
                aria-hidden="true"
                class="shrink-0 text-polaris-muted-foreground"
              />
              <span class="truncate">{{ authLabel(tool.auth_type) }}</span>
            </span>
          </TableCell>
          <TableCell
            class="hidden align-top text-polaris-muted-foreground @3xl:table-cell"
          >
            {{ updated(tool) }}
          </TableCell>
          <TableCell v-if="canManage" class="pe-2 align-top">
            <RowMenu
              :label="
                t('POLARIS.PAGES.TOOLS_TABLE.ROW_ACTIONS', { name: tool.title })
              "
              :items="items"
              @action="action => emit('action', { action, id: tool.id })"
            />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </div>
</template>
